import { useEffect } from "react";
import { Helmet } from "react-helmet-async";

/**
 * The static index.html ships sitewide description / og / twitter tags as a
 * fallback for non-JS crawlers. Once Helmet owns the head, drop those static
 * copies so each route exposes exactly one of each tag.
 */
const STATIC_SELECTORS = [
  "meta[name='description']",
  "meta[property='og:title']",
  "meta[property='og:description']",
  "meta[property='og:type']",
  "meta[name='twitter:title']",
  "meta[name='twitter:description']",
  "meta[name='twitter:card']",
];

const useDedupeStaticHead = () => {
  useEffect(() => {
    for (const selector of STATIC_SELECTORS) {
      document
        .querySelectorAll(`${selector}:not([data-rh])`)
        .forEach((tag) => tag.remove());
    }
  }, []);
};

const SITE_NAME = "Arjan Sethi";

interface SeoProps {
  title: string;
  description: string;
  path: string;
  type?: "website" | "article" | "profile";
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
}

/**
 * Per-route head metadata: unique title, description, self-referencing
 * canonical, matching Open Graph / Twitter tags and optional JSON-LD.
 */
const Seo = ({ title, description, path, type = "website", jsonLd, noindex }: SeoProps) => {
  useDedupeStaticHead();

  const fullTitle = title.includes(SITE_NAME) ? title : `${title}, ${SITE_NAME}`;
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={path} />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={path} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />

      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
